import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  BookOpen,
  CheckSquare,
  ClipboardList,
  Download,
  FileText,
  Lock,
} from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Seal } from "@/components/brand/Seal";
import { useAuth } from "@/hooks/useAuth";
import { LeadMagnetForm } from "@/components/library/LeadMagnetForm";
import { getFeaturedLeadMagnet } from "@/lib/leadMagnet.functions";
import {
  getMyLibraryTier,
  listPublishedResources,
  requestLibraryDownload,
  type LibraryResourcePublic,
} from "@/lib/familyLibrary.functions";

export const Route = createFileRoute("/academy/library")({
  head: () => ({
    meta: [
      { title: "The Family Library — Currency Thoughts" },
      {
        name: "description",
        content:
          "Downloadable workbooks, checklists, and templates for families building generational wealth. Free for Library Members and above.",
      },
      { property: "og:title", content: "The Family Library — Currency Thoughts" },
      {
        property: "og:description",
        content:
          "Practical tools — workbooks, checklists, and guides — for the families we serve.",
      },
    ],
    links: [{ rel: "canonical", href: "/academy/library" }],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["family-library", "published"],
        queryFn: () => listPublishedResources(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["family-library", "lead-magnet"],
        queryFn: () => getFeaturedLeadMagnet(),
      }),
    ]);
  },
  component: LibraryPage,
});

const FILTERS = [
  { id: "all", label: "All" },
  { id: "workbook", label: "Workbooks" },
  { id: "checklist", label: "Checklists" },
  { id: "template", label: "Templates" },
  { id: "guide", label: "Guides" },
] as const;

const TIER_RANK: Record<string, number> = {
  public: 0,
  library_member: 1,
  family_member: 2,
  legacy_member: 3,
};
const REQUIRED_RANK: Record<string, number> = {
  library: 1,
  family: 2,
  legacy: 3,
};

const CATEGORY_LABEL: Record<string, string> = {
  workbook: "Workbook",
  checklist: "Checklist",
  template: "Template",
  guide: "Guide",
};

const TIER_LABEL: Record<string, string> = {
  library: "Library Member",
  family: "Family Member",
  legacy: "Legacy Member",
};

function CategoryIcon({ category }: { category: string }) {
  const cls = "h-8 w-8";
  if (category === "workbook") return <BookOpen className={cls} strokeWidth={1.2} />;
  if (category === "checklist") return <CheckSquare className={cls} strokeWidth={1.2} />;
  if (category === "template") return <ClipboardList className={cls} strokeWidth={1.2} />;
  return <FileText className={cls} strokeWidth={1.2} />;
}

function LibraryPage() {
  const { user } = useAuth();
  const { data: items } = useSuspenseQuery({
    queryKey: ["family-library", "published"],
    queryFn: () => listPublishedResources(),
  });
  const { data: leadMagnet } = useSuspenseQuery({
    queryKey: ["family-library", "lead-magnet"],
    queryFn: () => getFeaturedLeadMagnet(),
  });
  const tierFn = useServerFn(getMyLibraryTier);
  const downloadFn = useServerFn(requestLibraryDownload);
  const { data: tierData } = useQuery({
    queryKey: ["family-library", "my-tier", user?.id ?? "anon"],
    queryFn: () => tierFn(),
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const tier = tierData?.tier ?? "public";
  // Membership tier gates entitlement; firm staff can open anything for review.
  const userRank = tierData?.isStaff ? 99 : TIER_RANK[tier] ?? 0;

  const [filter, setFilter] = useState<string>("all");
  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.category === filter)),
    [items, filter],
  );

  async function handleDownload(item: LibraryResourcePublic) {
    try {
      const { url } = await downloadFn({ data: { id: item.id } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Download failed");
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* HERO */}
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-5xl px-5 py-16 text-center md:px-10 md:py-20">
            <div className="flex justify-center">
              <Eyebrow color="cream">The Family Library</Eyebrow>
            </div>
            <h1 className="mt-5 font-display text-[40px] leading-[1.1] md:text-[56px]">
              Practical tools for the{" "}
              <em className="italic text-bronze-400">families we serve.</em>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl font-sans text-[17px] leading-relaxed text-cream-50/80">
              Downloadable workbooks, checklists, and templates you can use today.
              Library Members and above enjoy full access.
            </p>
          </div>
        </section>

        {/* FILTERS */}
        {leadMagnet && (
          <section className="bg-cream-50 py-12">
            <div className="mx-auto max-w-6xl px-5 md:px-10">
              <LeadMagnetForm resource={leadMagnet} />
            </div>
          </section>
        )}

        <section className="border-b border-bronze-600/15 bg-cream-50 py-8">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal-500">
                Browse
              </span>
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`px-4 py-1.5 font-sans text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                      filter === f.id
                        ? "bg-navy-800 text-cream-50"
                        : "border border-bronze-600/30 bg-white text-charcoal-900 hover:border-bronze-600"
                    }`}
                    style={{ borderRadius: 999 }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* GRID */}
        <section className="bg-cream-50 py-14">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            {filtered.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((it) => {
                  const needRank = REQUIRED_RANK[it.tier_required] ?? 1;
                  const authorized = userRank >= needRank;
                  return (
                    <ResourceCard
                      key={it.id}
                      item={it}
                      authorized={authorized}
                      onDownload={() => handleDownload(it)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* DISCLAIMER */}
        <section className="bg-cream-50 pb-16">
          <div className="mx-auto max-w-3xl px-5 text-center md:px-10">
            <p className="font-sans text-[12px] leading-relaxed text-charcoal-500">
              Workbooks and checklists are provided for educational purposes only
              and do not constitute legal, tax, or investment advice. Policy
              benefits are governed by the actual contract issued by the
              insurance carrier.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ResourceCard({
  item,
  authorized,
  onDownload,
}: {
  item: LibraryResourcePublic;
  authorized: boolean;
  onDownload: () => void;
}) {
  return (
    <article
      className="flex h-full flex-col overflow-hidden border-t-[3px] border-t-bronze-600 border-x border-b border-bronze-600/15 bg-white shadow-[0_8px_24px_-18px_rgba(168,139,92,0.4)]"
      style={{ borderRadius: 6 }}
    >
      <div className="relative flex aspect-[16/9] items-center justify-center overflow-hidden bg-navy-800">
        {item.cover_image_url ? (
          <img
            src={item.cover_image_url}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center text-bronze-400">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-bronze-400/60">
              <CategoryIcon category={item.category} />
            </div>
            <span className="mt-3 font-sans text-[10px] uppercase tracking-[0.22em]">
              {CATEGORY_LABEL[item.category] ?? item.category}
            </span>
          </div>
        )}
        <span className="absolute left-3 top-3 bg-cream-50/95 px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] text-bronze-600">
          {CATEGORY_LABEL[item.category] ?? item.category}
        </span>
        {!authorized && (
          <div className="absolute inset-0 flex items-center justify-center bg-navy-900/55">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cream-50/95 text-navy-800">
              <Lock className="h-5 w-5" strokeWidth={1.6} />
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-px bg-bronze-600/60" />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-[18px] leading-snug text-navy-900">
          {item.title}
        </h3>
        {item.description && (
          <p className="mt-2 line-clamp-2 font-sans text-[13px] leading-relaxed text-charcoal-500">
            {item.description}
          </p>
        )}
        <p className="mt-3 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
          {TIER_LABEL[item.tier_required] ?? item.tier_required} access ·{" "}
          {item.download_count.toLocaleString()} downloads
        </p>
        <div className="mt-5">
          {authorized ? (
            <PrimaryButton onClick={onDownload} className="w-full">
              <Download className="h-4 w-4" /> Download
            </PrimaryButton>
          ) : (
            <Link
              to="/membership"
              className="flex w-full items-center justify-center gap-2 border border-bronze-600 bg-cream-50 px-5 py-2.5 font-sans text-[12px] font-semibold uppercase tracking-[0.14em] text-navy-800 transition-colors hover:bg-bronze-400 hover:text-navy-900"
              style={{ borderRadius: 2 }}
            >
              <Lock className="h-3.5 w-3.5" /> Become a Library Member →
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center border border-bronze-600/20 bg-white p-12 text-center">
      <Seal title="CT" subtitle="Library" size="sm" />
      <h3 className="mt-6 font-display text-[22px] text-navy-900">
        Family Library coming soon
      </h3>
      <p className="mt-3 font-sans text-[14px] leading-relaxed text-charcoal-500">
        New workbooks and checklists are being prepared for the families we
        serve. Check back soon — or book a consultation in the meantime.
      </p>
      <Link
        to="/book"
        className="mt-6 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600"
      >
        Book a Consultation →
      </Link>
    </div>
  );
}